<template>
  <app-content class="original-voucher-list frame-page">
    <div class="page-header">
      <div>
        <h2>原始凭证管理</h2>
        <p>查看识别完成的原始凭证，追踪OCR状态与标准订单/流水的写入情况。</p>
      </div>
      <div class="header-actions">
        <Button type="primary" icon="h-icon-upload" @click="goWorkbench">识别工作台</Button>
        <Button icon="h-icon-refresh" :loading="loading" @click="refresh">刷新</Button>
      </div>
    </div>

    <div class="h-panel filter-panel">
      <div class="h-panel-body">
        <Form :labelWidth="90" inline>
          <FormItem label="单据类型">
            <Select
              v-model="filters.docType"
              :datas="docTypeOptions"
              keyName="value"
              titleName="label"
              placeholder="全部类型"
            />
          </FormItem>
          <FormItem label="业务状态">
            <Select
              v-model="filters.bizStatus"
              :datas="bizStatusOptions"
              keyName="value"
              titleName="label"
              placeholder="全部状态"
            />
          </FormItem>
          <FormItem label="OCR状态">
            <Select
              v-model="filters.ocrStatus"
              :datas="ocrStatusOptions"
              keyName="value"
              titleName="label"
              placeholder="全部状态"
            />
          </FormItem>
          <FormItem label="平台/关键字">
            <input
              v-model="filters.keyword"
              type="text"
              class="h-input"
              placeholder="平台、店铺或业务编号"
              @keyup.enter="handleSearch"
            >
          </FormItem>
          <FormItem>
            <Button type="primary" @click="handleSearch">查询</Button>
            <Button @click="resetFilters">重置</Button>
          </FormItem>
        </Form>
      </div>
    </div>

    <div class="h-panel table-panel">
      <div class="h-panel-bar">
        <span class="h-panel-title">原始凭证列表</span>
        <span class="total-text">共 {{ pagination.total }} 条</span>
      </div>
      <div class="h-panel-body">
        <div v-if="!list.length && !loading" class="empty-state">
          <i class="h-icon-empty"></i>
          <p>暂无原始凭证，请先通过识别或批量导入生成。</p>
        </div>

        <div v-else class="table-wrapper">
          <table class="voucher-table">
            <thead>
              <tr>
                <th style="width: 80px">ID</th>
                <th style="width: 120px">类型</th>
                <th style="width: 160px">平台/账户</th>
                <th style="width: 180px">业务编号</th>
                <th style="width: 150px">业务日期</th>
                <th style="width: 110px">OCR状态</th>
                <th style="width: 120px">业务状态</th>
                <th style="width: 160px">创建时间</th>
                <th style="width: 110px">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in list" :key="item.id">
                <td>{{ item.id }}</td>
                <td>
                  <div class="doc-type">{{ getDocTypeLabel(item.docType) }}</div>
                </td>
                <td>
                  <div>{{ item.platform || '-' }}</div>
                  <div class="secondary-text">{{ item.storeOrAccountCode || '-' }}</div>
                </td>
                <td>
                  <div>{{ item.bizNo || '-' }}</div>
                </td>
                <td>{{ formatDateTime(item.bizDate) || '-' }}</td>
                <td>
                  <span class="status-tag" :class="`ocr-${(item.ocrStatus || '').toLowerCase()}`">
                    {{ getOcrStatusLabel(item.ocrStatus) }}
                  </span>
                </td>
                <td>
                  <span class="status-tag" :class="getBizStatusClass(item.bizStatus)">
                    {{ getBizStatusLabel(item.bizStatus) }}
                  </span>
                </td>
                <td>{{ formatDateTime(item.createdAt) }}</td>
                <td>
                  <button class="link-button" @click="viewDetail(item)">查看</button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="table-footer">
            <Pagination
              v-model="pagination"
              layout="pager"
              :pageSize="pagination.size"
              align="right"
              @change="handlePageChange"
            />
          </div>
        </div>

        <div v-if="loading" class="loading-state">
          <i class="h-icon-loading"></i>
          <span>加载中...</span>
        </div>
      </div>
    </div>

    <Modal
      v-model="detailVisible"
      title="原始凭证详情"
      width="900"
      :mask-closable="false"
    >
      <div v-if="detailLoading" class="loading-state modal-loading">
        <i class="h-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-else-if="detailData.voucher" class="detail-wrapper">
        <section class="detail-section">
          <h4>基础信息</h4>
          <table class="detail-table">
            <tbody>
              <tr>
                <th>原始凭证ID</th>
                <td>{{ detailData.voucher.id }}</td>
                <th>单据类型</th>
                <td>{{ getDocTypeLabel(detailData.voucher.docType) }}</td>
              </tr>
              <tr>
                <th>平台/渠道</th>
                <td>{{ detailData.voucher.platform || '-' }}</td>
                <th>店铺/账户</th>
                <td>{{ detailData.voucher.storeOrAccountCode || '-' }}</td>
              </tr>
              <tr>
                <th>业务编号</th>
                <td>{{ detailData.voucher.bizNo || '-' }}</td>
                <th>业务日期</th>
                <td>{{ formatDateTime(detailData.voucher.bizDate) || '-' }}</td>
              </tr>
              <tr>
                <th>OCR状态</th>
                <td>{{ getOcrStatusLabel(detailData.voucher.ocrStatus) }}</td>
                <th>业务状态</th>
                <td>{{ getBizStatusLabel(detailData.voucher.bizStatus) }}</td>
              </tr>
              <tr>
                <th>OCR摘要</th>
                <td colspan="3">{{ detailData.voucher.ocrTextSummary || '无' }}</td>
              </tr>
              <tr>
                <th>原始文件路径</th>
                <td colspan="3">
                  <span>{{ detailData.voucher.originFilePath || '-' }}</span>
                  <Button
                    v-if="detailData.voucher.originFilePath"
                    type="text"
                    @click="openFile(detailData.voucher.originFilePath)"
                  >打开</Button>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <section v-if="detailData.order" class="detail-section">
          <h4>标准订单</h4>
          <table class="detail-table">
            <tbody>
              <tr>
                <th>订单号</th>
                <td>{{ detailData.order.orderNo }}</td>
                <th>订单状态</th>
                <td>{{ detailData.order.orderStatus || '-' }}</td>
              </tr>
              <tr>
                <th>买家</th>
                <td>{{ detailData.order.buyerName || '-' }}</td>
                <th>下单时间</th>
                <td>{{ formatDateTime(detailData.order.orderTime) || '-' }}</td>
              </tr>
              <tr>
                <th>币种</th>
                <td>{{ detailData.order.currency || '-' }}</td>
                <th>净额</th>
                <td>{{ formatCurrency(detailData.order.netAmount, detailData.order.currency) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="sub-table-wrapper">
            <table class="sub-table">
              <thead>
                <tr>
                  <th style="width: 60px">行号</th>
                  <th>商品标题</th>
                  <th style="width: 120px">SKU</th>
                  <th style="width: 80px">数量</th>
                  <th style="width: 120px">单价</th>
                  <th style="width: 120px">金额</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in detailData.orderItems" :key="item.id">
                  <td>{{ item.lineNo }}</td>
                  <td>{{ item.title || '-' }}</td>
                  <td>{{ item.sku || '-' }}</td>
                  <td>{{ item.qty || '-' }}</td>
                  <td>{{ formatCurrency(item.unitPrice, detailData.order.currency) }}</td>
                  <td>{{ formatCurrency(item.lineAmount, detailData.order.currency) }}</td>
                </tr>
                <tr v-if="!detailData.orderItems.length">
                  <td colspan="6" class="empty-cell">暂无明细</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section v-if="detailData.transactions && detailData.transactions.length" class="detail-section">
          <h4>标准交易流水 ({{ detailData.transactions.length }} 条)</h4>
          <div class="sub-table-wrapper">
            <table class="sub-table">
              <thead>
                <tr>
                  <th style="width: 150px">时间</th>
                  <th style="width: 100px">类型</th>
                  <th>描述</th>
                  <th style="width: 100px">金额</th>
                  <th style="width: 100px">手续费</th>
                  <th style="width: 100px">净额</th>
                  <th style="width: 140px">参考号</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="txn in detailData.transactions" :key="txn.id">
                  <td>{{ formatDateTime(txn.txnTime) || '-' }}</td>
                  <td>{{ txn.txnType || '-' }}</td>
                  <td>{{ txn.description || '-' }}</td>
                  <td>{{ formatCurrency(txn.grossAmount, txn.currency) }}</td>
                  <td>{{ formatCurrency(txn.feeAmount, txn.currency) }}</td>
                  <td>{{ formatCurrency(txn.netAmount, txn.currency) }}</td>
                  <td>{{ txn.referenceId || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <div slot="footer">
        <Button @click="detailVisible = false">关闭</Button>
      </div>
    </Modal>
  </app-content>
</template>

<script>
export default {
  name: 'OriginalVoucherList',
  data() {
    return {
      loading: false,
      list: [],
      pagination: {
        page: 1,
        size: 20,
        total: 0
      },
      filters: {
        docType: '',
        bizStatus: '',
        ocrStatus: '',
        keyword: ''
      },
      docTypeOptions: [
        { label: '全部', value: '' },
        { label: '店铺订单', value: 'shop_order' },
        { label: '资金流水', value: 'stripe_statement' },
        { label: '银行回单', value: 'bank_slip' },
        { label: '发票', value: 'invoice' },
        { label: '其它', value: 'other' }
      ],
      bizStatusOptions: [
        { label: '全部', value: '' },
        { label: '未映射', value: 'UNMAPPED' },
        { label: 'OCR完成', value: 'OCR_DONE' },
        { label: '需复核', value: 'NEED_REVIEW' },
        { label: '已写入标准单据', value: 'MAPPED' },
        { label: '已归并', value: 'GROUPED' },
        { label: '已关联', value: 'ASSOCIATED' },
        { label: '已生成凭证', value: 'VOUCHED' }
      ],
      ocrStatusOptions: [
        { label: '全部', value: '' },
        { label: '已上传', value: 'UPLOADED' },
        { label: '识别完成', value: 'OCR_DONE' },
        { label: '识别失败', value: 'FAILED' },
        { label: '需复核', value: 'NEED_REVIEW' }
      ],
      detailVisible: false,
      detailLoading: false,
      detailData: {
        voucher: null,
        order: null,
        orderItems: [],
        transactions: []
      },
      docTypeMap: {
        shop_order: '店铺订单',
        stripe_statement: '资金流水',
        bank_slip: '银行回单',
        invoice: '发票',
        receipt: '收据',
        contract: '合同',
        loan_note: '借据',
        other: '其它'
      }
    }
  },
  mounted() {
    this.loadList()
  },
  methods: {
    loadList() {
      this.loading = true
      const params = {
        page: this.pagination.page,
        size: this.pagination.size
      }
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value) {
          params[key] = value
        }
      })
      this.$api.originalVoucher.list(params).then(({ data }) => {
        this.list = data.records || []
        this.pagination = {
          page: data.current,
          size: data.size,
          total: data.total
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.pagination.page = 1
      this.loadList()
    },
    resetFilters() {
      this.filters = {
        docType: '',
        bizStatus: '',
        ocrStatus: '',
        keyword: ''
      }
      this.handleSearch()
    },
    handlePageChange() {
      this.loadList()
    },
    refresh() {
      this.loadList()
    },
    getDocTypeLabel(code) {
      if (!code) return '其它'
      return this.docTypeMap[code.toLowerCase()] || this.docTypeMap[code] || code
    },
    getBizStatusLabel(status) {
      const map = {
        UNMAPPED: '未映射',
        OCR_DONE: 'OCR完成',
        NEED_REVIEW: '需复核',
        MAPPED: '已写入标准单据',
        GROUPED: '已归并',
        ASSOCIATED: '已关联',
        VOUCHED: '已生成凭证'
      }
      return map[status] || status || '-'
    },
    getOcrStatusLabel(status) {
      const map = {
        UPLOADED: '已上传',
        OCR_DONE: '识别完成',
        FAILED: '识别失败',
        NEED_REVIEW: '需复核'
      }
      return map[status] || status || '-'
    },
    getBizStatusClass(status) {
      if (!status) return ''
      return status.toLowerCase()
    },
    formatDateTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }
      return date.toLocaleString('zh-CN')
    },
    formatCurrency(value, currency) {
      if (value === undefined || value === null || value === '') {
        return '-'
      }
      const num = Number(value)
      if (Number.isNaN(num)) {
        return value
      }
      try {
        return new Intl.NumberFormat('zh-CN', {
          style: 'currency',
          currency: currency ? currency.toUpperCase() : 'CNY'
        }).format(num)
      } catch (e) {
        return num.toFixed(2)
      }
    },
    viewDetail(item) {
      this.detailVisible = true
      this.detailLoading = true
      this.detailData = {
        voucher: null,
        order: null,
        orderItems: [],
        transactions: []
      }
      this.$api.originalVoucher.detail(item.id).then(({ data }) => {
        this.detailData = {
          voucher: data.voucher,
          order: data.order,
          orderItems: data.orderItems || [],
          transactions: data.transactions || []
        }
      }).finally(() => {
        this.detailLoading = false
      })
    },
    openFile(path) {
      if (!path) return
      const isAbsolute = /^https?:\/\//i.test(path)
      let url = path
      if (!isAbsolute) {
        url = path.startsWith('/') ? `${window.location.origin}${path}` : `${window.location.origin}/${path}`
      }
      window.open(url, '_blank')
    },
    goWorkbench() {
      this.$router.push({ name: 'OriginalVoucherWorkbench' })
    }
  }
}
</script>

<style lang="less" scoped>
.original-voucher-list {
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 22px;
    }

    p {
      margin: 4px 0 0;
      color: @gray2-color;
    }
  }

  .header-actions {
    display: flex;
    gap: 12px;
  }

  .filter-panel {
    margin-bottom: 15px;
  }

  .table-panel {
    .total-text {
      margin-left: 10px;
      color: @gray2-color;
    }

    .table-wrapper {
      overflow-x: auto;
    }
  }

  .voucher-table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      border: 1px solid @gray4-color;
      padding: 10px;
      font-size: 13px;
      text-align: left;
    }

    th {
      background: #fafafa;
      color: #444;
    }

    .doc-type {
      font-weight: 600;
    }

    .secondary-text {
      color: @gray2-color;
      font-size: 12px;
      margin-top: 2px;
    }

    .link-button {
      background: none;
      border: none;
      color: #1890ff;
      cursor: pointer;
      padding: 0;
    }

    .link-button:hover {
      text-decoration: underline;
    }
  }

  .status-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    background: #f2f3f5;
    color: #666;

    &.ocr-uploaded {
      background: rgba(24, 144, 255, 0.1);
      color: #1890ff;
    }

    &.ocr-ocr_done {
      background: rgba(82, 196, 26, 0.15);
      color: #52c41a;
    }

    &.ocr-failed {
      background: rgba(245, 108, 108, 0.2);
      color: #f56c6c;
    }

    &.ocr-need_review {
      background: rgba(250, 173, 20, 0.2);
      color: #d89614;
    }

    &.unmapped {
      background: rgba(130, 130, 130, 0.15);
      color: #595959;
    }

    &.ocr_done {
      background: rgba(24, 144, 255, 0.15);
      color: #096dd9;
    }

    &.need_review {
      background: rgba(250, 173, 20, 0.2);
      color: #d48806;
    }

    &.mapped,
    &.grouped,
    &.associated,
    &.vouched {
      background: rgba(82, 196, 26, 0.15);
      color: #52c41a;
    }
  }

  .table-footer {
    margin-top: 15px;
  }

  .empty-state {
    text-align: center;
    color: @gray2-color;
    padding: 60px 0;

    i {
      font-size: 36px;
      display: block;
      margin-bottom: 10px;
    }
  }

  .loading-state {
    margin-top: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: @gray2-color;
  }

  .detail-section {
    margin-bottom: 20px;

    h4 {
      margin: 0 0 10px;
      font-size: 16px;
    }
  }

  .detail-table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      border: 1px solid @gray4-color;
      padding: 8px 10px;
      font-size: 13px;
      text-align: left;
    }

    th {
      width: 120px;
      background: #fafafa;
    }
  }

  .sub-table-wrapper {
    margin-top: 10px;
    overflow-x: auto;
  }

  .sub-table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      border: 1px solid @gray4-color;
      padding: 6px 8px;
      font-size: 12px;
    }

    th {
      background: #f7f8fa;
    }

    .empty-cell {
      text-align: center;
      color: @gray2-color;
    }
  }

  .modal-loading {
    justify-content: center;
  }
}
</style>
